// RunThumbs.tsx — a run as PICTURES: its frames (or the card / clip when those exist) left to right, the run's real
// progress, and its status — so a list of runs, or a growth part, shows what it IS instead of only ids and step names
// (2026-09-29: "there's no visuals anywhere, everything is text"). One read: the run's filmstrip. Polls while live.
import { useEffect, useState } from "react";
import { opsApi, type Filmstrip } from "@/api/ops.ts";

const LIVE = new Set(["pending", "running", "paused"]);

/** The picture for one beat: the card (frame + its text) if made, else the frame, else a clip's poster. Pure. */
export function beatPicture(b: Filmstrip["segments"][number]["beats"][number]): { src: string; kind: "card" | "frame" | "clip" } | null {
  const card = b.previews?.card?.posterUrl ?? b.production?.card?.url ?? null;
  if (card) return { src: card, kind: "card" };
  const frame = b.previews?.frame?.posterUrl ?? b.production?.image?.url ?? null;
  if (frame) return { src: frame, kind: "frame" };
  const clip = b.previews?.clip?.posterUrl ?? null;
  return clip ? { src: clip, kind: "clip" } : null;
}

export function RunThumbs({ runId, max = 6, height = 96, onOpen }: { runId: string; max?: number; height?: number; onOpen?: () => void }) {
  const [fs, setFs] = useState<Filmstrip | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true, t: ReturnType<typeof setTimeout> | undefined;
    const load = () => opsApi.filmstrip(runId).then((f) => {
      if (!alive) return; setFs(f); setErr(null);
      if (LIVE.has(String(f.run?.status))) t = setTimeout(load, 20_000);
    }).catch((e) => alive && setErr(String(e)));  // catch-reason: the error is shown to the person (setErr)
    void load();
    return () => { alive = false; if (t) clearTimeout(t); };
  }, [runId]);

  const beats = (fs?.segments ?? []).flatMap((s) => s.beats);
  const pics = beats.map(beatPicture);
  const run = fs?.run as (Filmstrip["run"] & { progress?: { done: number; total: number } | null; status?: string }) | undefined;
  const p = run?.progress;
  const status = run?.status ?? null;
  const w = Math.round(height * 9 / 16);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div onClick={onOpen} style={{ display: "flex", gap: 4, cursor: onOpen ? "pointer" : "default" }} title={onOpen ? "open the run" : undefined}>
        {err ? <div style={{ fontSize: 12, color: "#9ca3af" }}>pictures unavailable</div>
          : !fs ? Array.from({ length: Math.min(max, 4) }, (_, i) => <div key={i} style={{ width: w, height, borderRadius: 4, background: "#f3f4f6" }} />)
          : beats.length === 0 ? <div style={{ fontSize: 12, color: "#9ca3af" }}>no frames yet</div>
          : pics.slice(0, max).map((pic, i) => pic
              ? <img key={i} src={pic.src} alt={`beat ${i + 1} ${pic.kind}`} loading="lazy" style={{ width: w, height, objectFit: "cover", borderRadius: 4, background: "#f3f4f6" }} />
              : <div key={i} style={{ width: w, height, borderRadius: 4, background: "#f3f4f6", fontSize: 10, color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center" }}>…</div>)}
        {beats.length > max && <div style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>+{beats.length - max}</div>}
      </div>
      {fs && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {status && <span style={{ padding: "1px 8px", borderRadius: 8, marginRight: 6, background: status === "failed" ? "#fee2e2" : status === "succeeded" ? "#dcfce7" : status === "cancelled" ? "#f3f4f6" : "#dbeafe",
            color: status === "failed" ? "#b91c1c" : status === "succeeded" ? "#166534" : status === "cancelled" ? "#6b7280" : "#1d4ed8" }}>{status}</span>}
          {p?.total ? `${p.done} of ${p.total} steps` : null}
        </div>
      )}
    </div>
  );
}
